import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUp, Plus, PieChart, BarChart3, Sparkles, LineChart, ChevronRight } from 'lucide-react';
import SimplizeLogo from './SimplizeLogo';
import FlowingGradient from './FlowingGradient';
import { Storage } from '../constants/storage';

const SUGGESTIONS = [
    { icon: Sparkles, label: 'Nebula AI' },
    { icon: LineChart, label: 'Vẽ biểu đồ tài chính' },
    { icon: PieChart, label: 'Xây dựng danh mục' },
    { icon: BarChart3, label: 'Định giá cổ phiếu' },
];

const STATIC_LINES = [
    'Phân tích kỹ thuật cổ phiếu',
    'Định giá doanh nghiệp',
    'Xây dựng danh mục đầu tư',
    'Theo dõi tin tức thị trường',
];

const formatNum = (num) => {
    if (!num) return '0';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPct = (num) => {
    if (!num) return '0.00';
    const f = Math.abs(num).toFixed(2);
    return num >= 0 ? `+${f}` : `-${f}`;
};

const TICKER_TEMPLATES = {
    'VNINDEX': (t) => `VN-Index hôm nay ${t.pctChange >= 0 ? 'tăng' : 'giảm'} ${Math.abs(t.netChange).toFixed(2)} điểm`,
    '^DJI': (t) => `Dow Jones ${t.pctChange >= 0 ? 'tăng' : 'giảm'} ${formatPct(t.pctChange)}%`,
    '^N225': (t) => `Nikkei 225 ${t.pctChange >= 0 ? 'tăng' : 'giảm'} ${formatPct(t.pctChange)}%`,
    'GC=F': (t) => `Vàng thế giới đang ở mức $${formatNum(t.indexClose)}`,
    'CL=F': (t) => `Dầu thô WTI ${t.pctChange >= 0 ? 'tăng' : 'giảm'} ${formatPct(t.pctChange)}%`,
    'BTCUSDT': (t) => `Bitcoin đang giao dịch tại $${formatNum(t.indexClose)}`,
};

const buildLines = (data) => {
    const result = [];
    if (data && data.length > 0) {
        data.forEach((t) => {
            if (!t) return;
            const template = TICKER_TEMPLATES[t.ticker];
            if (template) result.push(template(t));
        });
    }
    STATIC_LINES.forEach((text) => result.push(text));
    return result;
};

const RotatingText = () => {
    const [lines, setLines] = useState(STATIC_LINES);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        const CACHE_KEY = Storage.market_data;
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
            try { setLines(buildLines(JSON.parse(cached))); } catch (e) {}
        }
        const fetchData = async () => {
            try {
                const res = await fetch('https://api2.simplize.vn/api/company/se/market/tickers?tickers=VNINDEX,%5EDJI,%5EN225,GC%3DF,CL%3DF,BTCUSDT');
                const json = await res.json();
                if (json.status === 200 && json.data) {
                    localStorage.setItem(CACHE_KEY, JSON.stringify(json.data));
                    setLines(buildLines(json.data));
                }
            } catch (e) {}
        };
        fetchData();
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setIsAnimating(true);
            setTimeout(() => {
                setCurrentIndex((prev) => (prev + 1) % lines.length);
                setIsAnimating(false);
            }, 600);
        }, 3000);
        return () => clearInterval(interval);
    }, [lines.length]);

    const currentText = lines[currentIndex % lines.length] || '';

    return (
        <div className="h-[36px] sm:h-[40px] w-full flex justify-center">
            <span
                key={currentText}
                className={`animated-gradient text-base sm:text-2xl md:text-[32px] font-semibold whitespace-nowrap text-neutral-800 transition-opacity duration-[600ms] ${isAnimating ? 'opacity-0' : 'opacity-100'}`}
            >
                {currentText}
            </span>
        </div>
    );
};

const HomeNew = () => {
    const navigate = useNavigate();
    const [inputValue, setInputValue] = useState('');

    const handleSend = () => {
        navigate('/login');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="h-screen bg-[#FBFAF9] flex flex-col overflow-hidden relative">
            <FlowingGradient />
            <div className="absolute left-0 top-0 z-[1] h-[350px] w-full bg-gradient-to-b from-white to-transparent pointer-events-none" />
            <div
                className="absolute z-[1] rounded-full bg-white pointer-events-none"
                style={{
                    width: 'calc(100% + 32px)',
                    height: 'calc(80%)',
                    left: '-16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    filter: 'blur(72px)',
                }}
            />
            <header className="relative z-[2] flex-shrink-0 w-full max-w-5xl mx-auto flex items-center justify-between px-6 sm:px-10 py-3 sm:py-4">
                <SimplizeLogo className="h-6 sm:h-7 w-auto" />
                <button
                    onClick={() => navigate('/login')}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800 transition-colors"
                >
                    Bắt đầu trải nghiệm
                </button>
            </header>

            <main className="relative z-[2] flex-1 flex flex-col items-center justify-center px-4 sm:px-6">
                <h1 className="text-2xl sm:text-4xl md:text-[46px] font-bold text-gray-900 text-center mb-2 sm:mb-6 leading-[1.2] tracking-tight">
                    Tôi có thể làm gì cho bạn?
                </h1>

                <div className="mb-6 sm:mb-12">
                    <RotatingText />
                </div>

                <div className="relative z-[2] w-full max-w-[820px]">
                    <div className="relative border border-gray-200/80 rounded-[20px] bg-white shadow-[0_2px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.1)] transition-shadow duration-300">
                        <div className="px-5 pt-4 sm:pt-5 pb-2 sm:pb-3 relative">
                            <input
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="w-full text-[16px] sm:text-[17px] text-gray-900 focus:outline-none bg-transparent"
                            />
                            {!inputValue && (
                                <span className="absolute left-5 top-4 sm:top-5 text-[16px] sm:text-[17px] text-gray-400 pointer-events-none typing-placeholder">
                                    Hỏi bất kỳ điều gì
                                </span>
                            )}
                        </div>

                        <div className="flex items-center justify-between px-4 sm:px-5 py-3">
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => navigate('/login')}
                                    className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
                                >
                                    <Plus className="w-4 h-4" strokeWidth={2} />
                                </button>
                            </div>
                            <button
                                onClick={handleSend}
                                className="w-8 h-8 rounded-full bg-gray-900 hover:bg-gray-800 flex items-center justify-center transition-colors shadow-sm"
                            >
                                <ArrowUp className="w-4 h-4 text-white" strokeWidth={2.5} />
                            </button>
                        </div>
                    </div>

                    <div className="mt-5 sm:mt-6">
                        <div className="flex items-center justify-center gap-2.5 flex-wrap sm:flex-nowrap">
                            {SUGGESTIONS.map((item, index) => {
                                const Icon = item.icon;
                                return (
                                    <button
                                        key={index}
                                        onClick={() => navigate('/login')}
                                        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-gray-200/80 bg-white text-[13.5px] text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 whitespace-nowrap flex-shrink-0 shadow-[0_1px_2px_rgba(0,0,0,0.04)] opacity-0 animate-[chipSlideIn_0.4s_ease-out_forwards]"
                                        style={{ animationDelay: `${index * 0.08 + 0.3}s` }}
                                    >
                                        <Icon className="w-4 h-4 text-gray-500" strokeWidth={1.8} />
                                        <span>{item.label}</span>
                                    </button>
                                );
                            })}
                            <button
                                onClick={() => navigate('/login')}
                                className="inline-flex items-center gap-1 px-3.5 py-2 rounded-full border border-gray-200/80 bg-white text-[13.5px] text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 whitespace-nowrap flex-shrink-0 shadow-[0_1px_2px_rgba(0,0,0,0.04)] opacity-0 animate-[chipSlideIn_0.4s_ease-out_forwards]"
                                style={{ animationDelay: `${SUGGESTIONS.length * 0.08 + 0.3}s` }}
                            >
                                <span>Thêm</span>
                                <ChevronRight className="w-4 h-4 text-gray-400" strokeWidth={2} />
                            </button>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default HomeNew;
